import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useDebounce } from '../../hooks/useDebounce';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { extractErrorMessage } from '../../utils/error-handler';
import { partnersService } from '../../services/partners.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import type { Partner } from '../../types/partners';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import { ProjectCell } from '../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn, buildDefaultFilters, buildColumnFiltersParam,
} from '../../components/common/saas-data-table';

const FILTER_DEFS: SaasColumn[] = [
  { field: 'name', header: 'Name', filter: true },
  { field: 'partnerType', header: 'Type', filter: true },
  { field: 'contactPerson', header: 'Contact', filter: true },
];

// Category filter options — fixed enum (matches PartnerCategory: INTERNAL | CUSTOMER).
const CATEGORY_OPTIONS = [
  { label: 'All Categories', value: '' },
  { label: 'Internal', value: 'INTERNAL' },
  { label: 'Customer', value: 'CUSTOMER' },
];

const PartnersList = ({ embedded = false, appId }: { embedded?: boolean; appId?: string }) => {
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.PARTNERS) && !appId;
  const canUpdate = useCanUpdate(JOB_NAMES.PARTNERS);
  const canDelete = useCanDelete(JOB_NAMES.PARTNERS);

  const [rows, setRows] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [searchText, setSearchText] = useState('');
  const [accessibleProjects, setAccessibleProjects] = useState<AccessibleProject[]>([]);
  // Shown for ONE project (the Data Explorer): the screen opens already
  // narrowed to it, instead of to the session's project.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(appId ?? null);
  // >1 accessible project ≈ platform/partner admin who may filter + change a partner's project.
  const canManageProjects = !appId && (accessibleProjects.length > 1);

  useEffect(() => {
    projectsService.getAccessible().then(setAccessibleProjects).catch(() => setAccessibleProjects([]));
  }, []);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useAppNavigate();

  // Category filter — routed through the `partnerCategory` column filter (exact
  // match, applied server-side; whitelisted in the partner repository).
  const setCategoryFilter = (value: string) =>
    onFilter({ filters: { ...(filters || {}), partnerCategory: { value: value || null, matchMode: 'equals' } } });

  const debouncedSearch = useDebounce(searchText, 500);
  const notify = (severity: 'success' | 'error', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'success' ? 'Success' : 'Error', detail, life: severity === 'error' ? 8000 : 3000 });

  const fetchRows = useCallback(async (
    page = 1, size = pageSize, sortBy = sortField, order = sortOrder, active = filters,
  ) => {
    try {
      setLoading(true);
      const params: any = { page, page_size: size };
      if (includeDeleted) params.include_deleted = true;
      if (debouncedSearch?.trim()) params.search = debouncedSearch.trim();
      if (sortBy) { params.sort_by = sortBy; params.sort_order = order === 1 ? 'asc' : 'desc'; }
      const fp = buildColumnFiltersParam(active);
      if (fp) params.filters = fp;
      if (selectedProjectId) params.app_id = selectedProjectId;
      const res = await partnersService.getAll(params);
      setRows(res.data || []);
      setTotalRecords(res.pagination?.totalItems ?? (res.pagination as any)?.total ?? 0);
    } catch (err: any) {
      notify('error', extractErrorMessage(err, 'Failed to load partners.'));
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, debouncedSearch, pageSize, sortField, sortOrder, filters, selectedProjectId]);

  useEffect(() => { setFirst(0); fetchRows(1, pageSize); }, [includeDeleted, debouncedSearch, selectedProjectId]); // eslint-disable-line

  const onPage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(Math.floor(e.first / e.rows) + 1, e.rows, sortField, sortOrder); };
  const onSort = (e: any) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); fetchRows(1, pageSize, e.sortField, e.sortOrder); };
  const onFilter = (e: any) => { const next = e.filters || {}; setFilters(next); setFirst(0); fetchRows(1, pageSize, sortField, sortOrder, next); };
  const refresh = () => fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await partnersService.delete(deleteId);
      setDeleteId(null);
      refresh();
    } catch (err: any) {
      setDeleteId(null);
      notify('error', extractErrorMessage(err, 'Failed to delete partner'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const doRestore = async (id: string) => {
    try { await partnersService.restore(id); notify('success', 'Partner restored'); refresh(); }
    catch (err: any) { notify('error', extractErrorMessage(err, 'Failed to restore partner')); }
  };

  const columns: SaasColumn<Partner>[] = useMemo(() => {
    const cols: SaasColumn<Partner>[] = [
      { field: 'name', header: 'Name', sortable: true, filter: true, hideable: false },
      {
        field: 'partnerCategory',
        header: 'Category',
        body: (r) => {
          const isCustomer = r.partnerCategory === 'CUSTOMER';
          return (
            <span
              className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                isCustomer
                  ? 'bg-indigo-50 text-indigo-700 ring-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-300'
                  : 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {isCustomer ? 'Customer' : 'Internal'}
            </span>
          );
        },
      },
      { field: 'partnerType', header: 'Type', sortable: true, filter: true, body: (r) => r.partnerType || '-' },
      { field: 'contactPerson', header: 'Contact', filter: true, body: (r) => r.contactPerson || '-' },
      { field: 'contactPhoneNumber', header: 'Phone', body: (r) => r.contactPhoneNumber || '-' },
    ];
    if (includeDeleted) {
      cols.push({
        field: 'status', header: 'Status',
        body: (r) => {
          const d = (r as any).isDelete;
          return <span className={d ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}>{d ? 'Deleted' : 'Active'}</span>;
        },
      });
    }
    cols.push({
      field: 'appId', header: 'Project',
      body: (r) => <ProjectCell appId={r.appId} projects={accessibleProjects} />,
    });
    cols.push({
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => {
        const isDeleted = (r as any).isDelete;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View" className="p-2 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20" onClick={() => navigate(`/partners/${r._id}`)} data-pr-tooltip="View">
              <i className="pi pi-eye text-emerald-600" style={{ fontSize: '1.05rem' }} />
            </button>
            {!isDeleted && canUpdate && (
              <button aria-label="Edit" className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20" onClick={() => navigate(`/partners/${r._id}/edit`)} data-pr-tooltip="Edit">
                <i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} />
              </button>
            )}
            {!isDeleted && canDelete && (
              <button aria-label="Delete" className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setDeleteId(r._id)} data-pr-tooltip="Delete">
                <i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} />
              </button>
            )}
            {isDeleted && canDelete && (
              <button aria-label="Refresh" className="p-2 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20" onClick={() => doRestore(r._id)} data-pr-tooltip="Restore">
                <i className="pi pi-refresh text-emerald-600" style={{ fontSize: '1.05rem' }} />
              </button>
            )}
          </div>
        );
      },
    });
    return cols;
  }, [canUpdate, canDelete, includeDeleted, canManageProjects, accessibleProjects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.PARTNERS}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-Partners-List">
        {!embedded && (
          <PageHeader
            icon="handshake" title="Partners" subtitle="External organisations this project integrates with"
          />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="partners" columns={appId ? columns.filter((c) => c.field !== 'appId') : columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No partners found" first={first} rows={pageSize} totalRecords={totalRecords}
            onPage={onPage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sortField || undefined}
            sortOrder={sortOrder || undefined} onSort={onSort} filters={filters} onFilter={onFilter}
            globalSearch={searchText} onGlobalSearchChange={setSearchText} globalSearchPlaceholder="Search partners..."
            testId="PartnersList"
            toolbarStart={
              <div className="flex items-center gap-3 flex-wrap">
                {canManageProjects && (
                  <Dropdown
                    value={selectedProjectId}
                    options={accessibleProjects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id }))}
                    onChange={(e: any) => setSelectedProjectId(e.value || null)}
                    placeholder="Project"
                    showClear
                    style={{ minWidth: '160px' }}
                  />
                )}
                <Dropdown
                  value={filters?.partnerCategory?.value ?? ''}
                  options={CATEGORY_OPTIONS}
                  onChange={(e: any) => setCategoryFilter(e.value || '')}
                  placeholder="All Categories"
                  style={{ minWidth: '160px' }}
                  data-testid="PartnersList.Filter.Category"
                />
              </div>
            }
            toolbarEnd={
              <>
                <span className="text-sm text-ink-muted whitespace-nowrap">{totalRecords} {totalRecords === 1 ? 'record' : 'records'}</span>
                <RefreshButton onClick={refresh} loading={loading} />
                {canCreate && <Button label="Create Partner" icon="pi pi-plus" size="small" onClick={() => navigate('/partners/create')} />}
              </>
            }
          />
        </div>

        <DeleteConfirmDialog
          visible={!!deleteId} onCancel={() => setDeleteId(null)} onDelete={doDelete}
          message="Are you sure you want to delete this partner? It will be marked as deleted." loading={deleteLoading}
        />
      </div>
    </PermissionGuard>
  );
};

export default PartnersList;
